import { computed, reactive, ref } from "vue"
import type { BankBootstrapResult } from "../types/bank"
import { logger } from "../utils/logger"
import { firestoreState, updateUserPreferences } from "./firestoreStore"
import { isPersonalContext } from "./householdStore"
import { type BankPhase, clearResume, readResume, writeResume } from "./onboardingPersist"

export type OnboardingStep =
  | "welcome"
  | "ai_setup"
  | "income"
  | "accounts"
  | "categories"
  | "first_expense"
  | "complete"

interface BankSetupState {
  phase: BankPhase | "idle" | "error"
  connectionId: string | null
  /** Findings from the bootstrap pass. In memory only — re-fetchable. */
  derived: BankBootstrapResult | null
}

interface OnboardingState {
  currentStep: OnboardingStep
  isActive: boolean
  incomeAdded: boolean
  accountAdded: boolean
  categoryCustomized: boolean
  expenseAdded: boolean
  bank: BankSetupState
}

// The linear manual flow. "ai_setup" is intentionally NOT part of this sequence:
// OnboardingView branches at "welcome" into either the AI "ai_setup" path or this
// manual path, driving those transitions explicitly via goToStep. Keeping the
// linear helpers (nextStep / previousStep / stepProgress) defined over just the
// manual steps preserves their original semantics.
const ONBOARDING_STEPS: OnboardingStep[] = ["welcome", "income", "accounts", "categories", "first_expense", "complete"]

// Every step goToStep is allowed to navigate to, including the branched AI step,
// which branches off "welcome" rather than sitting in the linear flow. Bank
// setup used to be a branch here too; it's now a standalone post-purchase flow
// (see startBankSetup below) since bank sync is a paid feature.
const ALL_STEPS: OnboardingStep[] = ["welcome", "ai_setup", ...ONBOARDING_STEPS.slice(1)]

const state = reactive<OnboardingState>({
  currentStep: "welcome",
  isActive: false,
  incomeAdded: false,
  accountAdded: false,
  categoryCustomized: false,
  expenseAdded: false,
  bank: { phase: "idle", connectionId: null, derived: null },
})

// Resolved once, at module load, before Vue mounts — a bank redirect has to be
// answerable synchronously (see resumeBankSetupIfPending / isResumingBankSetup),
// and that can only be true if the breadcrumb is already in hand by mount.
const resumeSnapshot = ref(readResume())

// Whether the standalone post-purchase bank-setup overlay is on screen. The bank
// flow used to be a branch of onboarding; it's now offered after a user
// subscribes (bank sync is a paid feature), driven by the same phase machine.
const bankSetupActive = ref(false)

// Check if user needs onboarding (new user with no data)
export const needsOnboarding = computed(() => {
  // If onboarding is already active, continue showing it
  if (state.isActive) return true

  // Onboarding is a new-USER flow, not a per-context one. Preferences and
  // data are per-context, so a freshly created household context has neither
  // — evaluating it there would ambush the user with the full Welcome wizard
  // right after the create-household flow auto-switches into it.
  if (!isPersonalContext.value) return false

  // Check if user has completed onboarding before (stored in preferences)
  const preferences = firestoreState.preferences.value
  if (preferences?.onboardingCompleted) return false

  // An interrupted first run must resume. This has to sit ABOVE the has-data
  // check: once the user adds their first income on step 1, a reload (mobile
  // background kill, PWA refresh) would otherwise read that as "returning user
  // with data" and drop them on the dashboard, having silently skipped the rest
  // of the flow with no way back except Settings.
  if (preferences?.onboardingStarted) return true

  // Check if user already has data (returning user or data imported)
  const hasIncome = firestoreState.income.value.length > 0
  const hasAccounts = firestoreState.accounts.value.length > 0
  const hasExpenses = firestoreState.expenses.value.length > 0

  // If user has any meaningful data, skip onboarding
  if (hasIncome || hasAccounts || hasExpenses) return false

  return true
})

export const currentStep = computed(() => state.currentStep)
export const isOnboardingActive = computed(() => state.isActive)

export const currentStepIndex = computed(() => {
  return ONBOARDING_STEPS.indexOf(state.currentStep)
})

export const totalSteps = computed(() => ONBOARDING_STEPS.length)

export const canGoBack = computed(() => {
  return currentStepIndex.value > 0 && state.currentStep !== "complete"
})

export const canGoForward = computed(() => {
  return currentStepIndex.value < ONBOARDING_STEPS.length - 1
})

export const stepProgress = computed(() => {
  // Don't count welcome and complete in progress
  const progressSteps: OnboardingStep[] = ["income", "accounts", "categories", "first_expense"]
  const currentIdx = progressSteps.indexOf(state.currentStep)
  if (currentIdx === -1) {
    if (state.currentStep === "welcome") return 0
    if (state.currentStep === "complete") return 100
    return 0
  }
  return Math.round(((currentIdx + 1) / progressSteps.length) * 100)
})

// Actions
export function startOnboarding() {
  state.isActive = true
  state.currentStep = "welcome"
  // Best-effort breadcrumb so an interrupted first run resumes (see
  // needsOnboarding). Failure is fine — anonymous users may not have writable
  // preferences, which just leaves the previous behaviour.
  void updateUserPreferences({ onboardingStarted: true }).catch((error) => {
    logger.warn("Could not record onboarding start", {
      error: error instanceof Error ? error.message : String(error),
    })
  })
}

/**
 * The single entry point for showing onboarding at startup. Returns whether it
 * should be on screen.
 *
 * Kept separate from startOnboarding() rather than folded into it: that one
 * resets to "welcome" by design, and restartOnboarding() (the Settings "replay
 * the tour" action) depends on exactly that. Resuming needs the opposite.
 */
export function beginOrResumeOnboarding(_uid: string | null): boolean {
  // Bank redirects no longer belong to onboarding — they resume the standalone
  // bank-setup overlay instead (see resumeBankSetupIfPending, called separately
  // by App.vue). Onboarding itself has nothing to resume across a redirect, so
  // this just decides whether a first run is needed.
  if (!needsOnboarding.value) return false
  startOnboarding()
  return true
}

export const isBankSetupActive = computed(() => bankSetupActive.value)

/**
 * Open the standalone post-purchase bank-setup overlay from a clean slate. Used
 * when a freshly subscribed user is offered the "connect your bank" step.
 */
export function startBankSetup(): void {
  bankSetupActive.value = true
  state.bank = { phase: "idle", connectionId: null, derived: null }
}

/**
 * Close the bank-setup overlay and drop any redirect breadcrumb. Called when the
 * user finishes, backs out, or dismisses it.
 */
export function endBankSetup(): void {
  bankSetupActive.value = false
  exitBankSetup()
}

/**
 * Reopen the bank-setup overlay mid-redirect. On web the connect flow navigates
 * the page away and this whole heap is rebuilt on the way back, so the only way
 * to know a bank link is in flight — and which phase/connection it left off at —
 * is the breadcrumb resolved at module load. Returns whether it reopened.
 *
 * The uid check keeps a stale snapshot from a previous user out of the current
 * user's session.
 */
export function resumeBankSetupIfPending(uid: string | null): boolean {
  const snap = resumeSnapshot.value
  if (!snap) return false
  if (uid && snap.userId !== uid) {
    clearResume()
    resumeSnapshot.value = null
    return false
  }
  bankSetupActive.value = true
  state.bank.phase = snap.bankPhase
  state.bank.connectionId = snap.connectionId
  return true
}

/**
 * Whether a bank redirect belongs to onboarding rather than to Settings.
 *
 * Answers synchronously off the resolved snapshot, which matters: the only other
 * signal — "is the onboarding overlay up" — is set by a watch on firestore
 * init, and the bank redirect handlers run at mount, before that. Keying on the
 * overlay would make this a coin flip and let the Settings path steal a redirect
 * onboarding started.
 *
 * The uid check keeps a stale snapshot from a previous user out of the current
 * user's Settings link.
 */
export function isResumingBankSetup(uid: string | null): boolean {
  const snap = resumeSnapshot.value
  if (!snap) return false
  return !uid || snap.userId === uid
}

/**
 * Record where we are in the bank flow, so a redirect can be walked back from.
 *
 * Called before handing off to the bank and at each phase change after. The
 * findings themselves are never persisted — they come back from the bootstrap
 * endpoint, which is idempotent.
 */
export function setBankPhase(phase: BankPhase, connectionId: string | null, uid: string | null): void {
  state.bank.phase = phase
  state.bank.connectionId = connectionId
  if (!uid) return
  writeResume({ userId: uid, bankPhase: phase, connectionId })
  resumeSnapshot.value = readResume()
}

export function setBankDerived(derived: BankBootstrapResult | null): void {
  state.bank.derived = derived
}

export function failBankSetup(): void {
  state.bank.phase = "error"
}

/**
 * Stand down the bank flow without ending onboarding — used when the user backs
 * out of the bank, or when we fall through to the manual steps. Drops the
 * breadcrumb so a later reload doesn't resurrect a flow the user has left.
 */
export function exitBankSetup(): void {
  state.bank.phase = "idle"
  state.bank.connectionId = null
  clearResume()
  resumeSnapshot.value = null
}

export function nextStep() {
  const currentIdx = ONBOARDING_STEPS.indexOf(state.currentStep)
  if (currentIdx < ONBOARDING_STEPS.length - 1) {
    state.currentStep = ONBOARDING_STEPS[currentIdx + 1]
  }
}

export function previousStep() {
  const currentIdx = ONBOARDING_STEPS.indexOf(state.currentStep)
  if (currentIdx > 0) {
    state.currentStep = ONBOARDING_STEPS[currentIdx - 1]
  }
}

export function goToStep(step: OnboardingStep) {
  if (ALL_STEPS.includes(step)) {
    state.currentStep = step
  }
}

export function markIncomeAdded() {
  state.incomeAdded = true
}

export function markAccountAdded() {
  state.accountAdded = true
}

export function markCategoryCustomized() {
  state.categoryCustomized = true
}

export function markExpenseAdded() {
  state.expenseAdded = true
}

export async function completeOnboarding() {
  state.isActive = false
  state.currentStep = "welcome"
  // Drop the resume breadcrumb: it sits above every other gate, so leaving it
  // behind would reopen onboarding on the next load no matter what.
  exitBankSetup()

  // Save completion status to user preferences
  try {
    await updateUserPreferences({ onboardingCompleted: true, onboardingStarted: false })
  } catch (error) {
    // If saving fails, still complete onboarding locally
    // User preferences might not be available for anonymous users
    logger.warn("Could not save onboarding completion status", {
      error: error instanceof Error ? error.message : String(error),
    })
  }
}

/** Re-run the onboarding flow from the Welcome step. Used by Settings to let
 *  returning users replay the tour. Clears the persisted "completed" flag so
 *  the gate in `needsOnboarding` opens again. */
export async function restartOnboarding(): Promise<void> {
  resetOnboarding()
  state.isActive = true
  try {
    await updateUserPreferences({ onboardingCompleted: false, onboardingStarted: true })
  } catch (error) {
    logger.warn("Could not reset onboarding completion status", {
      error: error instanceof Error ? error.message : String(error),
    })
  }
}

export function skipOnboarding() {
  completeOnboarding()
}

// Reset for testing
export function resetOnboarding() {
  state.currentStep = "welcome"
  state.isActive = false
  state.incomeAdded = false
  state.accountAdded = false
  state.categoryCustomized = false
  state.expenseAdded = false
  state.bank.derived = null
  // Also clears the breadcrumb — a replayed tour must not inherit a dead bank
  // phase from a link the user made weeks ago.
  exitBankSetup()
}

// Export state for components
export const onboardingState = {
  currentStep: computed(() => state.currentStep),
  isActive: computed(() => state.isActive),
  incomeAdded: computed(() => state.incomeAdded),
  accountAdded: computed(() => state.accountAdded),
  categoryCustomized: computed(() => state.categoryCustomized),
  expenseAdded: computed(() => state.expenseAdded),
  bankPhase: computed(() => state.bank.phase),
  bankConnectionId: computed(() => state.bank.connectionId),
  bankDerived: computed(() => state.bank.derived),
}
